import { useMemo } from 'react'

import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { hasRevenuePermissions } from '@/features/authorization/permissionHandlers/revenuePermissions'
import { LEGACY_MARKET_PROGRAMS } from '@/features/site/constants'
import { useInsightRevenuePeriodsQuery } from '@/features/site/hooks/useInsightRevenuePeriodsQuery'
import { useMarketProgramsQuery } from '@/features/site/hooks/useMarketProgramsQuery'
import { useRevenuePeriodQuery } from '@/features/site/hooks/useRevenuePeriodQuery'
import type { MarketProgram } from '@/features/site/types/marketProgram'
import type { InsightRevenuePeriod, RevenuePeriod } from '@/features/site/types/revenue'
import type { Site } from '@/features/site/types/site'
import { getMarketProgramsOptions } from '@/features/site/utils/revenueUtils/marketProgramOptions'

type UseRevenueTabsDataParams = {
  site: Site | null
}

type UseRevenueTabsDataResult = {
  // Loading states
  isLoading: boolean
  // Data
  insightRevenuePeriods: InsightRevenuePeriod[]
  legacyRevenuePeriods: RevenuePeriod[] | null
  marketPrograms: MarketProgram[] | null
  // Tab visibility based on data
  hasRevenueData: boolean
  hasLegacyData: boolean
}

export function useRevenueTabsData(params: UseRevenueTabsDataParams): UseRevenueTabsDataResult {
  const { loggedInUser } = useAuth()
  const { site } = params

  const canFetchRevenueData = useMemo(
    () => (loggedInUser ? hasRevenuePermissions({ loggedInUser, site }) : false),
    [loggedInUser, site],
  )

  const siteUuid = site?.uuid ?? ''
  const timezone = site?.timezone

  // Fetch revenue data from Insight API
  const { revenuePeriods: insightRevenuePeriods, isFetching: isFetchingInsight } = useInsightRevenuePeriodsQuery(
    {
      siteUuid,
      timezone,
    },
    {
      enabled: canFetchRevenueData && Boolean(siteUuid),
    },
  )

  // Fetch legacy revenue data
  const { revenuePeriods: legacyRevenuePeriods, isFetching: isFetchingLegacy } = useRevenuePeriodQuery(
    { customerUuid: siteUuid },
    { enabled: canFetchRevenueData && Boolean(siteUuid) },
  )

  // Fetch market programs to map legacy revenue periods to market program types
  const { marketPrograms, isFetching: isFetchingMarketPrograms } = useMarketProgramsQuery({
    enabled: canFetchRevenueData,
  })

  // Memoize derived values to prevent unnecessary re-renders
  return useMemo(() => {
    const isLoading = isFetchingInsight || isFetchingLegacy || isFetchingMarketPrograms

    // If Insight API returns data, show the Revenue view
    const hasRevenueData = insightRevenuePeriods.length > 0

    // If legacy API returns data with legacy market programs, show the Legacy view
    const legacyMarketPrograms = getMarketProgramsOptions(marketPrograms, legacyRevenuePeriods)
    const hasLegacyData = legacyMarketPrograms.some((mp) => mp.type && LEGACY_MARKET_PROGRAMS.includes(mp.type))

    return {
      isLoading,
      insightRevenuePeriods,
      legacyRevenuePeriods,
      marketPrograms,
      hasRevenueData,
      hasLegacyData,
    }
  }, [
    isFetchingInsight,
    isFetchingLegacy,
    isFetchingMarketPrograms,
    insightRevenuePeriods,
    legacyRevenuePeriods,
    marketPrograms,
  ])
}
